import React from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import {
  selectResultsHistory,
  selectActiveResultIndex,
} from "../../store/selectors/aiResults";
import { aiSetActiveResultMessageId } from "../../store/actions/dashboard/dashboard";

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 4px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.divider};
  margin-bottom: 10px;
  flex-shrink: 0;
  min-width: 0;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.divider};
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text.secondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Counter = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
`;

const Question = styled.span`
  font-size: 13px;
  font-style: italic;
  color: ${({ theme }) => theme.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const LatestPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ theme }) =>
    theme.type === "light" ? theme.gray.gray100 : theme.gray.gray800};
  color: ${({ theme }) => theme.text.secondary};
  border: 1px solid ${({ theme }) => theme.divider};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
  }
`;

export default function ResultHistoryHeader() {
  const dispatch = useDispatch();
  const history = useSelector(selectResultsHistory);
  const activeIndex = useSelector(selectActiveResultIndex);

  if (history.length === 0 || activeIndex < 0) return null;

  const active = history[activeIndex];
  const total = history.length;
  const isLatest = activeIndex === total - 1;

  const goTo = (idx) => {
    if (idx < 0 || idx >= total) return;
    dispatch(aiSetActiveResultMessageId(history[idx].messageId));
  };

  return (
    <HeaderWrapper>
      <LeftGroup>
        <NavButton
          type="button"
          onClick={() => goTo(activeIndex - 1)}
          disabled={activeIndex === 0}
          aria-label="Previous result"
          title="Previous result"
        >
          <FontAwesomeIcon icon={["fal", "chevron-left"]} size="xs" />
        </NavButton>
        <Counter>
          {activeIndex + 1} of {total}
        </Counter>
        <NavButton
          type="button"
          onClick={() => goTo(activeIndex + 1)}
          disabled={isLatest}
          aria-label="Next result"
          title="Next result"
        >
          <FontAwesomeIcon icon={["fal", "chevron-right"]} size="xs" />
        </NavButton>
        {active.question ? (
          <Question title={active.question}>
            &ldquo;{active.question}&rdquo;
          </Question>
        ) : null}
      </LeftGroup>
      {!isLatest ? (
        <LatestPill
          type="button"
          onClick={() => dispatch(aiSetActiveResultMessageId(null))}
          title="Jump to latest result"
        >
          <FontAwesomeIcon icon={["fal", "times"]} size="xs" />
          Latest
        </LatestPill>
      ) : null}
    </HeaderWrapper>
  );
}
